import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../design/dialog";
import {
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from "../design/fieldset";
import { Input } from "../design/input";
import { Button } from "../design/button";
import { SideType, sideTypes } from "../models/SideType";
import { Select } from "../design/select";
import { useFiling } from "../contexts/FilingContext";
import { Filing } from "../models/Filing";
import DragAndDropFile from "../DragAndDropFile";

interface Context {
  openDialog: (onCreated?: (filingId: Filing["_id"]) => void) => void;
  closeDialog: () => void;
  isOpen: boolean;
}

interface Errors {
  file?: string;
  name?: string;
  side?: string;
}

const CreateFilingDialogProviderContext = createContext<Context | undefined>(
  undefined,
);

export const CreateFilingDialogProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { createFiling } = useFiling();
  const [isOpen, setIsOpen] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState<string>("");
  const [filingDate, setFilingDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [side, setSide] = useState<SideType>("me");
  const [isUploading, setIsUploading] = useState(false);
  const [onCreatedCallback, setOnCreatedCallback] = useState<
    ((filingId: Filing["_id"]) => void) | undefined
  >(undefined);

  const openDialog = (onCreated?: (filingId: Filing["_id"]) => void) => {
    setOnCreatedCallback(() => onCreated);
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
  };

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!name.trim()) newErrors.name = "A name is required.";
    if (!file) newErrors.file = "A file is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (file) {
      setName(file.name);
    }
  }, [file]);

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setErrors({});
    const droppedFile = e.dataTransfer.files?.[0];
    if (!droppedFile) return;
    if (
      ![
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ].includes(droppedFile.type)
    )
      return setErrors({
        file: "Invalid file type. Only PDF and Word files are allowed.",
      });
    if (droppedFile.size > 10 * 1024 * 1024)
      // 10 MB limit
      return setErrors({ file: "File size exceeds 10 MB." });
    setFile(droppedFile);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsUploading(true);

    try {
      const filingId = await createFiling(file!, {
        name,
        filingDate: filingDate ? new Date(filingDate) : undefined,
        side,
      });

      clearForm();
      clearForm();

      if (onCreatedCallback) onCreatedCallback(filingId);
      closeDialog();
    } catch (error) {
      setErrors({ name: "Failed to create filing. Please try again." });
    }

    setIsUploading(false);
  };

  const clearForm = () => {
    setFile(null);
    setName("");
    setFilingDate(new Date().toISOString().split("T")[0]);
    setSide("me");
    setErrors({});
    setIsOpen(false);
  };

  return (
    <CreateFilingDialogProviderContext.Provider
      value={{ openDialog, closeDialog, isOpen }}
    >
      <Dialog open={isOpen} onClose={closeDialog}>
        <DialogTitle>Upload Filing</DialogTitle>
        {!file && (
          <DialogDescription>
            Filings are official documents submitted to the court, such as
            declarations, motions, or orders. Upload a PDF or Word document to
            track it in your case timeline and prepare any responses if needed.
          </DialogDescription>
        )}
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              {!file ? (
                <DragAndDropFile onFileDrop={setFile} />
              ) : (
                <>
                  <Field>
                    <Label>Name</Label>
                    <Input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Name"
                    />
                    {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                  </Field>
                  <Field>
                    <Label>Filing Date</Label>
                    <Input
                      type="date"
                      value={filingDate}
                      onChange={(e) => setFilingDate(e.target.value)}
                    />
                  </Field>
                  <Field>
                    <Label>Side</Label>
                    <Select
                      value={side}
                      onChange={(e) => setSide(e.target.value as SideType)}
                    >
                      <option value="" disabled>
                        Select side
                      </option>
                      {Object.entries(sideTypes).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ))}
                    </Select>
                    {errors.side && <ErrorMessage>{errors.side}</ErrorMessage>}
                  </Field>
                </>
              )}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          {file && (
            <Button plain onClick={() => setFile(null)}>
              Back
            </Button>
          )}
          {!file && (
            <Button plain onClick={clearForm}>
              Cancel
            </Button>
          )}
          <Button
            disabled={!file || !name.trim() || isUploading}
            onClick={handleSubmit}
          >
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
      {children}
    </CreateFilingDialogProviderContext.Provider>
  );
};

export const useCreateFilingDialog = () => {
  const ctx = useContext(CreateFilingDialogProviderContext);
  if (!ctx)
    throw new Error(
      "useCreateFilingDialog must be used inside CreateFilingDialogProvider",
    );
  return ctx;
};
